import { createRouter, createWebHistory } from 'vue-router'
import { getToken } from '@/utils/auth'
import store from '@/store'

// 路由组件懒加载
const Login = () => import('@/views/Login.vue')
const Register = () => import('@/views/Register.vue')
const ForgotPassword = () => import('@/views/ForgotPassword.vue')
const Dashboard = () => import('@/views/Dashboard.vue')
const Callback = () => import('@/views/Callback.vue')
const Profile = () => import('@/views/Profile.vue')
const NotFound = () => import('@/views/NotFound.vue')

const routes = [
    {
        path: '/',
        redirect: '/dashboard'
    },
    {
        path: '/login',
        name: 'Login',
        component: Login,
        meta: {
            title: '登录',
            guest: true, // 只有未登录用户可访问
            layout: 'auth'
        }
    },
    {
      path: '/register',
      redirect: '/login'
    },
    // {
    //     path: '/register',
    //     name: 'Register',
    //     component: Register,
    //     meta: {
    //         title: '注册',
    //         guest: true,
    //         layout: 'auth'
    //     }
    // },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: ForgotPassword,
        meta: {
            title: '忘记密码',
            guest: true,
            layout: 'auth'
        }
    },
    {
        path: '/dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: {
            title: '系统选择',
            requiresAuth: true, // 需要登录才能访问
            layout: 'main'
        }
    },
    {
        path: '/profile',
        name: 'Profile',
        component: Profile,
        meta: {
            title: '个人资料',
            requiresAuth: true,
            layout: 'main'
        }
    },
    {
        path: '/callback',
        name: 'Callback',
        component: Callback,
        meta: {
            title: '回调处理',
            layout: 'minimal'
        }
    },
    {
        path: '/404',
        name: 'NotFound',
        component: NotFound,
        meta: {
            title: '页面未找到',
            layout: 'minimal'
        }
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: '/404'
    }
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return { top: 0 }
        }
    }
})

// 添加重定向到 OSS 回调的函数
function redirectToOSSCallback(token) {
    const originalUrl = new URL(location.href)
    const mainDomain = getMainDomain(location.href)
    const baseUrl = `${originalUrl.protocol}//${mainDomain}`
    const target_url = baseUrl + '/chosepkg/chosepkgone?lineType=1'
    const callbackUrl = new URL('/oss/callback', baseUrl)
    callbackUrl.searchParams.set('target_url', target_url)
    callbackUrl.searchParams.set('token', token)

    console.log(mainDomain, callbackUrl.toString())
    window.location.href = callbackUrl.toString()
}

function getMainDomain(url) {
    const hostname = window.location.hostname
    const parts = hostname.split('.')

    if (parts.length <= 2) {
        return hostname
    } else if (parts.length === 3) {
        return parts[1] + '.' + parts[2]
    } else {
        return parts[parts.length - 3] + '.' + parts[parts.length - 2] + '.' + parts[parts.length - 1]
    }
}

// 全局路由守卫
router.beforeEach(async (to, from, next) => {
    // 设置页面标题
    if (to.meta.title) {
        document.title = `${to.meta.title} - 统一认证平台`
    }

    // 🔧 先处理重定向参数（在所有其他逻辑之前）
    const { redirect_uri, state, system } = to.query
    if (redirect_uri && (to.path === '/login' || to.path === '/register')) {
        // 来自其他系统的重定向，设置 redirectInfo
        store.commit('auth/SET_REDIRECT_INFO', {
            redirect_uri: decodeURIComponent(redirect_uri),
            state: state || Math.random().toString(36).substring(2),
            system: system || (redirect_uri ? new URL(redirect_uri).hostname : null) || '外部系统',
            original_path: to.path
        })

        console.log('🔄 设置重定向信息:', store.getters['auth/redirectInfo'])
    }

    // debugger;
    const token = getToken()
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

    // 如果页面需要认证但没有token
    if (requiresAuth && !token) {
        // 保存重定向信息
        store.commit('auth/SET_REDIRECT_INFO', {
            redirect_uri: to.fullPath,
            state: 'local_redirect',
            system: 'auth-portal',
            original_path: to.path
        })

        next('/login')
        return
    }

    // 如果有token，验证token有效性
    if (token && !store.getters['auth/authChecked']) {
        try {
            await store.dispatch('auth/checkAuthStatus')
        } catch (error) {
            console.error('认证检查失败:', error)
            // Token无效，清除并重定向到登录页
            if (requiresAuth) {
                next('/login')
                return
            }
        }
    }

    // 如果已经登录，访问登录/注册页面则重定向
    if ((to.path === '/login' || to.path === '/register') && store.getters['auth/isAuthenticated']) {
        const token = store.getters['auth/token']
        const redirectInfo = store.getters['auth/redirectInfo']

        console.log('🔍 已登录用户重定向信息:', redirectInfo)

        if (redirectInfo.redirect_uri && redirectInfo.redirect_uri !== '/login') {
            // 有重定向信息，跳转回原系统
            try {
                const originalUrl = new URL(redirectInfo.redirect_uri)
                const baseUrl = `${originalUrl.protocol}//${originalUrl.host}`
                const callbackUrl = new URL('/oss/callback', baseUrl)
                callbackUrl.searchParams.set('target_url', redirectInfo.redirect_uri)
                callbackUrl.searchParams.set('token', token)

                store.commit('auth/CLEAR_REDIRECT_INFO')
                window.location.href = callbackUrl.toString()
                return
            } catch (error) {
                console.error('系统重定向失败:', error)
                // 如果重定向失败，执行默认的OSS回调重定向
                redirectToOSSCallback(token)
                return
            }
        } else {
            // 没有重定向信息，执行默认的OSS回调重定向
            redirectToOSSCallback(token)
            return
        }
    }

    // 继续正常的路由处理
    next()
})

// 全局后置守卫
router.afterEach((to, from) => {
    // 页面访问日志
    console.log(`📍 路由变化: ${from.path} → ${to.path}`)

    // 清除加载状态
    store.commit('app/SET_LOADING', false)
})

export default router 